import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { shareReplay } from "rxjs/operators";

import { TRADABLES_TYPE } from "@helpers/constants";
import { Dictionary } from "@helpers/types";
import { convertArrayToDictionary, filterDictionary } from "@helpers/functions";

import { StateService } from "@core-services/state.service";
import { Tradable } from "../entities/tradable";
import { Metal } from "../entities/tradables/metal";
import { Cryptocoin } from "../entities/tradables/cryptocoin";
import { Etf } from "../entities/tradables/etf";
import { Etc } from "../entities/tradables/etc";
import { Index } from "../entities/tradables";
import { Stock } from "../entities/tradables/stock";
import { LeveragedToken } from "@modules/+new/core/tradables/domain/entities/tradables/leveraged_tokens";
import { TradableInfo } from "@modules/+new/core/tradables/domain/entities/tradable-info";

const initialState: Dictionary<Tradable> = {};

@Injectable({
	providedIn: "root"
})
export class TradablesStateService extends StateService<Dictionary<Tradable>> {
	public tradables$: Observable<Dictionary<Tradable>> = this.select((state) => state).pipe(
		shareReplay({ refCount: true, bufferSize: 1 })
	);
	public metals$: Observable<Dictionary<Metal>> = this.select((state) =>
		filterDictionary<Tradable, Metal>(state, (val) => val.type === TRADABLES_TYPE.METAL)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public cryptocoins$: Observable<Dictionary<Cryptocoin>> = this.select((state) =>
		filterDictionary<Tradable, Cryptocoin>(state, (val) => val.type === TRADABLES_TYPE.CRYPTO)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public etfs$: Observable<Dictionary<Etf>> = this.select((state) =>
		filterDictionary<Tradable, Etf>(state, (val) => val.type === TRADABLES_TYPE.ETF)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public etcs$: Observable<Dictionary<Etc>> = this.select((state) =>
		filterDictionary<Tradable, Etc>(state, (val) => val.type === TRADABLES_TYPE.ETC)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public leveragedTokens$: Observable<Dictionary<LeveragedToken>> = this.select((state) =>
		filterDictionary<Tradable, LeveragedToken>(state, (val) => val.type === TRADABLES_TYPE.LEVERAGED_TOKEN)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public index$: Observable<Dictionary<Index>> = this.select((state) =>
		filterDictionary<Tradable, Index>(state, (val) => val.type === TRADABLES_TYPE.INDEX)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	public stocks$: Observable<Dictionary<Stock>> = this.select((state) =>
		filterDictionary<Tradable, Stock>(state, (val) => val.type === TRADABLES_TYPE.STOCK)
	).pipe(shareReplay({ refCount: true, bufferSize: 1 }));

	constructor() {
		super(initialState);
	}

	public setTradables(tradables: Dictionary<Tradable>): void {
		this.setState({ ...tradables });
	}

	public addTradables(tradables: Tradable[]): void {
		this.setState({ ...this.state, ...convertArrayToDictionary(tradables, "id") });
	}

	public selectTradableById(id: string): Observable<Tradable> {
		return this.select((state) => state[id]);
	}

	public setTradableInfo(id: string, assetInfo: TradableInfo): void {
		const tradable: Tradable = this.state[id];
		this.setState({ ...this.state, [tradable.id]: { ...tradable, assetInfo } });
	}

	public infoLoaded(id: string): boolean {
		return !!this.state[id];
	}
}
